/**
 * Merge the complete static Organizer EMS menu with the current organization's
 * plan catalog. The static file owns hierarchy/labels/routes/keys; this helper
 * owns only the transient visibility, entitlement and route-availability state.
 */
export const isFeatureMenuItemInCatalog = (item, featureAccess, ogzuid) =>
	Boolean(
		item?.featureKey &&
			featureAccess?.catalogLoaded === true &&
			featureAccess?.ogzuid === ogzuid &&
			Object.prototype.hasOwnProperty.call(
				featureAccess?.enabledMap || {},
				item.featureKey
			)
	);

export const isFeatureMenuItemEnabled = (item, featureAccess, ogzuid) =>
	Boolean(
		item?.featureKey &&
			featureAccess?.catalogLoaded === true &&
			featureAccess?.ogzuid === ogzuid &&
			featureAccess?.enabledMap?.[item.featureKey] === true
	);

export const isFeatureMenuItemRoutable = (item, hasRoute) =>
	Boolean(
		item?.['route-name'] && (!hasRoute || hasRoute(item['route-name']))
	);

export const mergeFeatureMenu = (items, featureAccess, hasRoute, ogzuid) => {
	if (!Array.isArray(items)) return [];

	return items.reduce((visibleItems, item) => {
		if (item.visible === false) return visibleItems;
		if (
			item.featureKey &&
			!isFeatureMenuItemInCatalog(item, featureAccess, ogzuid)
		) {
			return visibleItems;
		}

		const nextItem = { ...item };
		if (Array.isArray(item.sub)) {
			nextItem.sub = mergeFeatureMenu(
				item.sub,
				featureAccess,
				hasRoute,
				ogzuid
			);
			if (nextItem.sub.length === 0 && !item['route-name']) {
				return visibleItems;
			}
		}

		if (item.featureKey) {
			nextItem.locked = !isFeatureMenuItemEnabled(
				item,
				featureAccess,
				ogzuid
			);
			nextItem.routable = isFeatureMenuItemRoutable(item, hasRoute);
		}

		visibleItems.push(nextItem);
		return visibleItems;
	}, []);
};

const getMenuLabel = (item, language) => {
	const normalizedLanguage = String(language || 'zh_tw')
		.toLowerCase()
		.replace('_', '-');
	const languageCode = normalizedLanguage.split('-')[0];
	const labels = item?.label || {};

	return (
		labels[normalizedLanguage] ||
		labels[languageCode] ||
		labels['zh-tw'] ||
		labels.en ||
		''
	);
};

/**
 * Resolve the denied feature's visible menu context from its canonical key.
 * The nearest non-feature ancestor is the breadcrumb and the feature leaf is
 * the page title. This is presentation-only and never participates in access
 * decisions.
 */
export const findFeatureMenuContext = (
	items,
	featureKey,
	language = 'zh_tw',
	routeName = ''
) => {
	if (!Array.isArray(items) || !featureKey) return null;

	let context = null;
	let fallbackContext = null;
	const normalizedRouteName = String(routeName || '');
	const walk = (nodes, ancestors = []) => {
		for (const item of nodes) {
			if (item?.featureKey === featureKey) {
				const parent = ancestors[ancestors.length - 1];
				const candidate = {
					featureKey,
					breadcrumb: getMenuLabel(parent, language),
					pageTitle: getMenuLabel(item, language),
					routeName: item?.['route-name'] || '',
				};
				if (!fallbackContext) fallbackContext = candidate;
				if (
					!normalizedRouteName ||
					candidate.routeName === normalizedRouteName
				) {
					context = candidate;
					return true;
				}
			}

			if (
				Array.isArray(item?.sub) &&
				walk(item.sub, [...ancestors, item])
			) {
				return true;
			}
		}

		return false;
	};

	walk(items);
	return context || fallbackContext;
};
